import React from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { isAnepTemplate } from '../../types/configTemplate';
import AuditHistoryTab from '../audit/AuditHistoryTab';
import { TemplateImpactPreviewModal } from './TemplateImpactPreviewModal';
import { AssignTemplateModal } from './AssignTemplateModal';
import { ReplaceDefaultDialog } from './ReplaceDefaultDialog';
import { useTemplateDetail, TemplateDetailTab } from './templateDetail/useTemplateDetail';
import { TemplateOverviewTab } from './templateDetail/TemplateOverviewTab';
import { TemplateConfigTab } from './templateDetail/TemplateConfigTab';

interface ConfigTemplateDetailModalProps {
  templateId: string | null;
  isOpen: boolean;
  canEdit?: boolean;
  /** SuperAdmin: may give the template to clients or devices in bulk. */
  canAssign?: boolean;
  onClose: () => void;
  onTemplateUpdated: () => void;
}

/**
 * One template: owner (ANEP or a client), gateway type, default flag, the
 * devices that use it, and its configuration. The state and the actions
 * live in `useTemplateDetail`; each tab is its own component.
 */
export const ConfigTemplateDetailModal: React.FC<ConfigTemplateDetailModalProps> = ({
  templateId,
  isOpen,
  canEdit = false,
  canAssign = false,
  onClose,
  onTemplateUpdated,
}) => {
  const { formatNumber, t } = useI18n();
  const state = useTemplateDetail(templateId, isOpen, onTemplateUpdated);
  const { template, activeTab, setActiveTab, isEditMode, isLoading, error, successMessage } = state;

  if (!isOpen) return null;

  if (state.isFetching && !template) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="config-template-detail-modal tpd" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title"><h2>{t('configTemplates.detail.loadingTitle')}</h2></div>
            <button type="button" className="close-button" onClick={onClose} aria-label={t('common.close')}>&times;</button>
          </div>
          <div className="modal-content tpd-state">
            <div className="tpl-spinner" />
            <p>{t('configTemplates.detail.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="config-template-detail-modal tpd" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <div className="modal-title"><h2>{t('configTemplates.detail.errorTitle')}</h2></div>
            <button type="button" className="close-button" onClick={onClose} aria-label={t('common.close')}>&times;</button>
          </div>
          <div className="modal-content">
            <div className="error-message" role="alert">{error || t('configTemplates.detail.notFound')}</div>
          </div>
          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>{t('common.close')}</button>
          </div>
        </div>
      </div>
    );
  }

  const tabs: Array<{ key: TemplateDetailTab; label: string; testId: string; count?: number; visible: boolean }> = [
    { key: 'overview', label: t('configTemplates.tabs.overview'), testId: 'tab-template-overview', visible: true },
    { key: 'config', label: t('configTemplates.tabs.config'), testId: 'tab-template-config', count: state.valueCount, visible: true },
    { key: 'json', label: t('configTemplates.tabs.json'), testId: 'tab-template-json', visible: state.showJsonTab },
    { key: 'history', label: t('configTemplates.tabs.history'), testId: 'template-history-tab-button', visible: state.canViewAuditLog },
  ];

  const ownerName = state.ownerName(template.client_id);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="config-template-detail-modal tpd" data-testid="modal-config-template-detail" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title tpd-title">
            <span className="tpd-mark" aria-hidden="true">{template.device_type}</span>
            <div className="tpd-title-text">
              <h2>{isEditMode ? t('configTemplates.detail.editTitle') : template.name}</h2>
              <div className="tpd-title-sub">
                <span className={isAnepTemplate(template.client_id) ? 'tpd-owner is-anep' : 'tpd-owner'}>{ownerName}</span>
                {template.is_default && <span className="default-badge" title={t('configTemplates.defaultHint')}>{t('configTemplates.defaultBadge')}</span>}
                {!template.is_active && <span className="tpd-off">{t('common.inactive')}</span>}
              </div>
            </div>
          </div>
          <button type="button" className="close-button" data-testid="btn-modal-close" onClick={onClose} aria-label={t('common.close')}>&times;</button>
        </div>

        <div className="modal-tabs" role="tablist">
          {tabs.filter((tab) => tab.visible).map((tab) => (
            <button
              key={tab.key}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.key}
              className={`tab-button ${activeTab === tab.key ? 'active' : ''}`}
              data-testid={tab.testId}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
              {tab.count !== undefined && <span className="ud-tab-count">{formatNumber(tab.count)}</span>}
            </button>
          ))}
        </div>

        <div className="modal-content tpd-body">
          {error && <div className="error-message" role="alert">{error}</div>}
          {successMessage && (
            <div className="success-banner">
              <span>{successMessage}</span>
              <button type="button" onClick={() => state.setSuccessMessage(null)} aria-label={t('common.close')}>&times;</button>
            </div>
          )}

          {activeTab === 'overview' && <TemplateOverviewTab state={state} />}
          {activeTab === 'config' && <TemplateConfigTab state={state} />}
          {activeTab === 'json' && state.showJsonTab && (
            <div className="tab-content">
              <section className="cu-section">
                <header className="cu-section-head"><h3>{t('configTemplates.rawJsonConfiguration')}</h3></header>
                <pre className="json-config-pre tpd-json">{JSON.stringify(template.json_config, null, 2)}</pre>
              </section>
            </div>
          )}
          {activeTab === 'history' && <AuditHistoryTab resourceType="config_template" resourceId={String(template.id)} />}
        </div>

        <div className="modal-actions tpd-footer">
          {isEditMode ? (
            <>
              <button type="button" className="btn btn-secondary" data-testid="btn-template-cancel" onClick={state.cancelEdit} disabled={isLoading}>
                {t('common.cancel')}
              </button>
              <button type="button" className="btn btn-secondary" data-testid="btn-template-preview" onClick={state.openPreview} disabled={isLoading} title={t('templateImpact.subtitle')}>
                {t('templateImpact.previewButton')}
              </button>
              <button type="button" className="btn btn-primary" data-testid="btn-template-save" onClick={state.save} disabled={isLoading}>
                {isLoading ? t('common.saving') : t('common.save')}
              </button>
            </>
          ) : (
            <>
              <button type="button" className="btn btn-secondary" data-testid="btn-template-close" onClick={onClose}>
                {t('common.close')}
              </button>
              {canAssign && template.is_active && (
                <button type="button" className="btn btn-secondary" data-testid="btn-template-assign" onClick={() => state.setShowAssign(true)}>
                  {t('configTemplates.assign.button')}
                </button>
              )}
              {canEdit && (
                <button type="button" className="btn btn-primary" data-testid="btn-template-edit" onClick={state.startEdit}>
                  {t('common.edit')}
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <TemplateImpactPreviewModal
        isOpen={state.showPreview}
        onClose={() => state.setShowPreview(false)}
        onConfirm={state.confirmFromPreview}
        confirming={isLoading}
        request={state.previewRequest}
      />

      {canAssign && (
        <AssignTemplateModal isOpen={state.showAssign} template={template} onClose={() => state.setShowAssign(false)} onAssigned={state.handleAssigned} />
      )}

      <ReplaceDefaultDialog
        existing={state.defaultConflict?.existing ?? null}
        ownerName={state.ownerName(state.ownerId)}
        deviceType={state.deviceType}
        newName={state.name.trim() || template.name}
        busy={isLoading}
        onReplace={() => state.resolveConflict(true)}
        onSaveWithoutDefault={() => state.resolveConflict(false)}
        onCancel={() => state.resolveConflict(null)}
      />
    </div>
  );
};
